/**
 * Bridge module: adapts OpenMosh WebGL2 GLSL effects to unstaglitch's
 * canvas ImageData format.
 *
 * OpenMosh effects use WebGL2 shaders (#version 300 es) with a fullscreen
 * quad, texture input, and FBO output. This bridge:
 *   1. Creates a WebGL2 context on a separate OffscreenCanvas
 *   2. Uploads the 2D canvas's ImageData as a texture
 *   3. Runs the effect shader (including prePasses for multi-pass effects)
 *   4. Reads back the result and puts it on the 2D canvas
 *
 * Animated effects use u_time — we pass a random time value for variety.
 * Feedback effects (melt, optical-flow) use u_feedback — we use the input
 * image as the feedback texture (first-frame behavior).
 */

const {
    VERTEX_SHADER,
    EFFECT_SHADERS,
    ANIMATED_EFFECTS
} = require('./openmosh-shaders');

const { EFFECT_DEFINITIONS } = require('./openmosh-definitions');

const { createProgram, getUniformLocations } = require('./openmosh-gl-utils');

// Effects to skip (require external resources or are 2D-canvas overlays)
const SKIP_EFFECTS = new Set([
    'tracking',  // 2D canvas overlay, no shader
    'ascii',     // requires glyph atlas texture
]);

// ---- Minimal WebGL2 renderer ----

class MiniGLRenderer {
    constructor() {
        this.gl = null;
        this.canvas = null;
        this.quadVAO = null;
        this.inputTexture = null;
        this.feedbackTexture = null;
        this.fbTexture = null;
        this.fbFBO = null;
        this.ppTextures = null;  // ping-pong for prePasses
        this.ppFBOs = null;
        this.programs = new Map();  // effectId -> { program, uniforms }
        this.passthrough = null;
        this.width = 0;
        this.height = 0;
    }

    _ensureGL(width, height) {
        if (this.gl && this.canvas) {
            if (this.canvas.width !== width) this.canvas.width = width;
            if (this.canvas.height !== height) this.canvas.height = height;
            this.width = width;
            this.height = height;
            return;
        }
        // Use a regular HTMLCanvasElement instead of OffscreenCanvas —
        // OffscreenCanvas with WebGL2 is not reliably supported in the main thread.
        // The canvas is kept off-DOM (never appended) so it's invisible.
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const gl = canvas.getContext('webgl2', {
            premultipliedAlpha: false,
            preserveDrawingBuffer: false
        });
        if (!gl) throw new Error('WebGL2 not supported');
        this.gl = gl;
        this.canvas = canvas;
        this.width = width;
        this.height = height;
        this._setupQuad();
        this.passthrough = this._compileProgram(
            VERTEX_SHADER,
            '#version 300 es\nprecision highp float;\nuniform sampler2D u_texture;\nin vec2 v_uv;\nout vec4 outColor;\nvoid main() { outColor = texture(u_texture, v_uv); }'
        );
    }

    _setupQuad() {
        const gl = this.gl;
        const vao = gl.createVertexArray();
        gl.bindVertexArray(vao);
        const buf = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
            -1, -1,  1, -1,  -1, 1,
            -1,  1,  1, -1,   1, 1,
        ]), gl.STATIC_DRAW);
        gl.enableVertexAttribArray(0);
        gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
        gl.bindVertexArray(null);
        this.quadVAO = vao;
    }

    _compileProgram(vs, fs) {
        const gl = this.gl;
        const program = createProgram(gl, vs, fs);
        const uniforms = getUniformLocations(gl, program);
        return { program, uniforms };
    }

    _getProgram(effectId, shaderDef) {
        if (this.programs.has(effectId)) return this.programs.get(effectId);
        const fs = shaderDef.fragment;
        const compiled = this._compileProgram(VERTEX_SHADER, fs);
        // Also compile prePass programs
        let prePassPrograms = null;
        if (shaderDef.prePasses) {
            prePassPrograms = shaderDef.prePasses.map(pp => ({
                program: this._compileProgram(VERTEX_SHADER, pp.fragment),
                linearFilter: pp.linearFilter
            }));
        }
        const entry = { program: compiled, prePassPrograms };
        this.programs.set(effectId, entry);
        return entry;
    }

    _createTexture(width, height, linearFilter = false) {
        const gl = this.gl;
        const tex = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, linearFilter ? gl.LINEAR : gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, linearFilter ? gl.LINEAR : gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        return tex;
    }

    _createFBO(texture) {
        const gl = this.gl;
        const fbo = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        return fbo;
    }

    _ensureBuffers(width, height) {
        const gl = this.gl;
        // Input texture (recreated on size change)
        if (this.inputTexture) gl.deleteTexture(this.inputTexture);
        this.inputTexture = this._createTexture(width, height);

        // Feedback texture (for melt, optical-flow)
        if (this.feedbackTexture) gl.deleteTexture(this.feedbackTexture);
        this.feedbackTexture = this._createTexture(width, height);

        // Output FBO texture
        if (this.fbTexture) gl.deleteTexture(this.fbTexture);
        this.fbTexture = this._createTexture(width, height);
        if (this.fbFBO) gl.deleteFramebuffer(this.fbFBO);
        this.fbFBO = this._createFBO(this.fbTexture);

        // Ping-pong textures for prePasses
        if (this.ppTextures) {
            this.ppTextures.forEach(t => gl.deleteTexture(t));
        }
        if (this.ppFBOs) {
            this.ppFBOs.forEach(f => gl.deleteFramebuffer(f));
        }
        this.ppTextures = [this._createTexture(width, height), this._createTexture(width, height)];
        this.ppFBOs = [this._createFBO(this.ppTextures[0]), this._createFBO(this.ppTextures[1])];
    }

    _uploadImageData(imageData) {
        const gl = this.gl;
        gl.bindTexture(gl.TEXTURE_2D, this.inputTexture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, this.width, this.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, imageData.data);
    }

    _uploadFeedback(imageData) {
        const gl = this.gl;
        gl.bindTexture(gl.TEXTURE_2D, this.feedbackTexture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, this.width, this.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, imageData.data);
    }

    _drawToCanvas(program, uniforms, inputTex, targetFBO, linearFilter = false) {
        const gl = this.gl;
        gl.bindFramebuffer(gl.FRAMEBUFFER, targetFBO);
        gl.viewport(0, 0, this.width, this.height);
        gl.useProgram(program.program);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, inputTex);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, linearFilter ? gl.LINEAR : gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, linearFilter ? gl.LINEAR : gl.NEAREST);
        gl.uniform1i(uniforms['u_texture'], 0);
        // Set flipY so v_uv maps correctly (flip for FBO, not for canvas)
        if (uniforms['u_flipY']) gl.uniform1f(uniforms['u_flipY'], 1.0);
        // Set resolution if needed
        if (uniforms['u_resolution']) gl.uniform2f(uniforms['u_resolution'], this.width, this.height);
        // Set time if needed
        if (uniforms['u_time']) gl.uniform1f(uniforms['u_time'], this._time || 0);
        // Bind feedback texture to slot 1
        if (uniforms['u_feedback']) {
            gl.activeTexture(gl.TEXTURE1);
            gl.bindTexture(gl.TEXTURE_2D, this.feedbackTexture);
            gl.uniform1i(uniforms['u_feedback'], 1);
            gl.activeTexture(gl.TEXTURE0);
        }
        gl.bindVertexArray(this.quadVAO);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
        gl.bindVertexArray(null);
    }

    _readBack() {
        const gl = this.gl;
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbFBO);
        const pixels = new Uint8Array(this.width * this.height * 4);
        gl.readPixels(0, 0, this.width, this.height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        return pixels;
    }

    renderEffect(effectId, shaderDef, imageData, values) {
        const width = imageData.width;
        const height = imageData.height;

        this._ensureGL(width, height);
        const gl = this.gl;
        this._ensureBuffers(width, height);
        this._uploadImageData(imageData);
        this._uploadFeedback(imageData);  // use input as feedback (first-frame)
        this._time = Math.random() * 100;  // random time for animated effects

        const entry = this._getProgram(effectId, shaderDef);
        const { program, prePassPrograms } = entry;

        // Run prePasses (e.g., blur for glow/bloom)
        let currentTex = this.inputTexture;
        if (prePassPrograms && prePassPrograms.length > 0) {
            for (let i = 0; i < prePassPrograms.length; i++) {
                const pp = prePassPrograms[i];
                const targetFBO = this.ppFBOs[i % 2];
                this._drawToCanvas(pp.program, pp.program.uniforms, currentTex, targetFBO, pp.linearFilter);
                currentTex = this.ppTextures[i % 2];
            }
        }

        // Set effect-specific uniforms.
        // IMPORTANT: bind the program BEFORE setUniforms, otherwise the
        // uniforms are set on whatever program was bound by the previous
        // effect/prePass and silently fail ("location is not from the
        // associated program"). This is what broke stacking multiple om
        // effects — each effect's params never reached its own shader.
        const uniforms = program.uniforms;
        gl.useProgram(program.program);
        if (shaderDef.setUniforms) {
            shaderDef.setUniforms(gl, uniforms, values);
        }

        // Main pass: render to output FBO
        this._drawToCanvas(program, uniforms, currentTex, this.fbFBO, shaderDef.linearFilter);

        // Read back result — no Y-flip needed: texImage2D (without
        // UNPACK_FLIP_Y_WEBGL) maps ImageData row 0 to texture row 0
        // (bottom in WebGL), and readPixels reads from bottom-left,
        // so the two flips cancel out and the orientation is correct.
        const pixels = this._readBack();
        return new ImageData(new Uint8ClampedArray(pixels), width, height);
    }
}

// Singleton renderer (reused across effects)
let renderer = null;

function getRenderer() {
    if (!renderer) {
        renderer = new MiniGLRenderer();
    }
    return renderer;
}

// ---- Bridge functions ----

// Build a params object from positional args, using the effect's param defaults.
// Args from the UI come as strings — convert to numbers for range/checkbox
// params, and to the option value string for select params.
function buildParams(effectDef, args) {
    const values = {};
    effectDef.params.forEach((param, i) => {
        let val;
        if (i < args.length && args[i] !== undefined && args[i] !== null) {
            val = args[i];
        } else {
            val = param.defaultValue;
        }
        if (param.type === 'range' || param.type === 'checkbox') {
            val = Number(val);
        } else if (param.type === 'select') {
            // The knob sends an integer index — convert to the option value string
            if (typeof val === 'number' || /^\d+$/.test(String(val))) {
                const idx = Math.round(Number(val));
                val = param.options[idx]?.value ?? param.defaultValue;
            }
        }
        values[param.key] = val;
    });
    return values;
}

// Create a bridged function for a single OpenMosh effect.
// Returns a function (canvas, ctx, ...args) => Promise<void>.
function createBridge(effectId, effectDef, shaderDef) {
    return async (canvas, ctx, ...args) => {
        const width = canvas.width;
        const height = canvas.height;
        const imageData = ctx.getImageData(0, 0, width, height);
        const values = buildParams(effectDef, args);

        const r = getRenderer();
        const resultImageData = r.renderEffect(effectId, shaderDef, imageData, values);
        ctx.putImageData(resultImageData, 0, 0);
    };
}

// Build bridged functions for all OpenMosh effects.
function buildBridgedEffects() {
    const bridged = {};
    const defMap = new Map(EFFECT_DEFINITIONS.map(d => [d.id, d]));

    for (const [id, shaderDef] of Object.entries(EFFECT_SHADERS)) {
        if (SKIP_EFFECTS.has(id)) continue;
        const def = defMap.get(id);
        if (!def) continue;
        bridged[id] = createBridge(id, def, shaderDef);
    }
    return bridged;
}

// Build fx.js-compatible descriptors from all OpenMosh effects.
function buildFxDescriptors(bridgedEffects) {
    const descriptors = [];
    const defMap = new Map(EFFECT_DEFINITIONS.map(d => [d.id, d]));

    for (const [id, shaderDef] of Object.entries(EFFECT_SHADERS)) {
        if (SKIP_EFFECTS.has(id)) continue;
        const def = defMap.get(id);
        if (!def) continue;

        const inputs = def.params.map(param => {
            if (param.type === 'select') {
                // Map select to a knob with integer values
                const optionCount = param.options.length;
                return {
                    name: param.label || param.key,
                    type: 'knob',
                    min: 0,
                    max: optionCount - 1,
                    step: 1,
                    value: Math.max(0, param.options.findIndex(o => o.value === param.defaultValue))
                };
            }
            if (param.type === 'checkbox') {
                return {
                    name: param.label || param.key,
                    type: 'knob',
                    min: 0,
                    max: 1,
                    step: 1,
                    value: param.defaultValue
                };
            }
            // range type
            return {
                name: param.label || param.key,
                type: 'knob',
                min: param.min,
                max: param.max,
                step: param.step,
                value: param.defaultValue
            };
        });

        descriptors.push({
            name: 'om-' + id,
            inputs,
            fn: bridgedEffects[id]
        });
    }
    return descriptors;
}

// Get list of OpenMosh effect IDs (for worker fxNameMap).
function getEffectIds() {
    return Object.keys(EFFECT_SHADERS).filter(id => !SKIP_EFFECTS.has(id));
}

module.exports = {
    buildBridgedEffects,
    buildFxDescriptors,
    createBridge,
    buildParams,
    getEffectIds,
    SKIP_EFFECTS
};
